import React from 'react';
import { ArrowLeft, Github, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import BookRecommendationSystem from "@/assets/achivements/BookRecommendationSystem.png";
import rgbook from '@/assets/achivements/rg-book.png';
import github_book from '@/assets/achivements/github_book.png';
const Book_Recommendation_System: React.FC = () => {
  return (
    <main className="min-h-screen bg-background pt-20">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">AI-Powered Book Recommender System</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">Python</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Artificial Intelligence</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Azure App Service</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Flask</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Recommendation Algorithms</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Data Analysis</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={BookRecommendationSystem} alt="Book Recommendation System - Overview" className="w-full object-cover" />
              </div>
              <div>
                <img src={rgbook} alt="Book Recommendation System - Overview" className="w-full object-cover" />
              </div>
              <div>
                <img src={github_book} alt="Book Recommendation System - Overview" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  ---- An AI-powered book recommendation system that uses machine learning algorithms to intelligently suggest 
                  books based on user preferences and reading history. This system leverages content-based filtering, TF-IDF 
                  vectorization, and cosine similarity to provide personalized book recommendations. Deployed on Azure App Service B1 
                  for cost-effective cloud hosting with reliable performance.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">AI-Powered Features</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The AI system intelligently recommends books using advanced algorithms:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li><strong>Content-Based Filtering:</strong> Finds books similar to ones you like</li>
                  <li><strong>TF-IDF Vectorization:</strong> Converts book information to numbers</li>
                  <li><strong>Cosine Similarity:</strong> Measures how similar books are</li>
                  <li><strong>Fuzzy Matching:</strong> Handles typos in book titles</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">AI Algorithm Workflow</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">Step 1: Data Loading</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Loads book data from CSV file</li>
                      <li>Cleans and prepares the data</li>
                      <li>Keeps essential info: title, author, rating, language</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">Step 2: Feature Creation</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Combines book title, author, and language into text</li>
                      <li>Uses TF-IDF to convert text to numerical vectors</li>
                      <li>Creates similarity matrix between all books</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">Step 3: Making Recommendations</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Find the input book in database</li>
                      <li>Calculate similarity with all other books</li>
                      <li>Return most similar books as recommendations</li>
                    </ul>
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">AI Capabilities</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Simple & Explainable</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Clean, readable code</li>
                      <li>Easy to understand algorithms</li>
                      <li>Well-documented functions</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Robust</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Handles typos with fuzzy matching</li>
                      <li>Error handling for missing books</li>
                      <li>Data validation and cleaning</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Visual</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Data distribution charts</li>
                      <li>Author popularity graphs</li>
                      <li>Rating vs pages analysis</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Interactive</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Command line interface</li>
                      <li>Web interface with Flask</li>
                      <li>Real-time recommendations</li>
                    </ul>
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">AI Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Built using Python with Flask web framework, scikit-learn for AI/ML algorithms, pandas for data manipulation, 
                  and numpy for numerical computations. The AI system is deployed on Azure App Service B1 tier, providing cost-effective 
                  cloud hosting with automatic scaling and monitoring capabilities for intelligent recommendations.
                </p>
                
                <h4 className="text-lg font-medium text-foreground mb-2">AI Algorithm Explanation</h4>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-base font-medium text-foreground mb-1">TF-IDF (Term Frequency-Inverse Document Frequency)</h5>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm">
                      <li><strong>Term Frequency:</strong> How often a word appears in a book's description</li>
                      <li><strong>Inverse Document Frequency:</strong> How rare/common a word is across all books</li>
                      <li><strong>Result:</strong> Important words get higher scores</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-base font-medium text-foreground mb-1">Cosine Similarity</h5>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm">
                      <li>Measures angle between two vectors</li>
                      <li>Range: 0 (completely different) to 1 (identical)</li>
                      <li>Works well for text similarity</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-base font-medium text-foreground mb-1">Content-Based Filtering</h5>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm">
                      <li>Recommends items similar to what user likes</li>
                      <li>Based on item features (title, author, genre)</li>
                      <li>No need for other users' data</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Structure</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-primary">----</span>
                    <span className="text-muted-foreground">book-recommendation-system/</span>
                  </div>
                  <div className="ml-4 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-primary">---------</span>
                      <span className="text-foreground">books.py</span>
                      <span className="text-muted-foreground text-xs"># Main recommendation system</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-primary">---------</span>
                      <span className="text-foreground">app.py</span>
                      <span className="text-muted-foreground text-xs"># Web application</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-primary">---------</span>
                      <span className="text-foreground">requirements.txt</span>
                      <span className="text-muted-foreground text-xs"># Dependencies</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-primary">---------</span>
                      <span className="text-foreground">books.csv</span>
                      <span className="text-muted-foreground text-xs"># Book dataset</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-primary">---------</span>
                      <span className="text-foreground">templates/</span>
                      <span className="text-muted-foreground text-xs"># HTML frontend</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Example Usage</h3>
                <div className="bg-muted/20 p-3 rounded-lg border border-border">
                  <pre className="text-xs text-muted-foreground overflow-x-auto">
{`from books import BookRecommendationSystem

# Initialize system
recommender = BookRecommendationSystem('books.csv')

# Get recommendations
recommendations = recommender.get_recommendations('Harry Potter', 5)

# Show statistics
recommender.show_stats()`}
                  </pre>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technical Stack</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Python</p>
                      <p className="text-sm text-muted-foreground">Core programming language</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">M</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Artificial Intelligence</p>
                      <p className="text-sm text-muted-foreground">AI recommendation algorithms</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">D</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Data Analysis</p>
                      <p className="text-sm text-muted-foreground">Pattern recognition</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Azure</p>
                      <p className="text-sm text-muted-foreground">Cloud platform</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">---- Quick Start</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">1. Install Dependencies</h4>
                    <div className="bg-muted/20 p-2 rounded border border-border">
                      <code className="text-sm text-muted-foreground">pip install -r requirements.txt</code>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">2. Run the System</h4>
                    <div className="space-y-2">
                      <div className="bg-muted/20 p-2 rounded border border-border">
                        <code className="text-sm text-muted-foreground"># Command line version</code><br/>
                        <code className="text-sm text-muted-foreground">python books.py</code>
                      </div>
                      <div className="bg-muted/20 p-2 rounded border border-border">
                        <code className="text-sm text-muted-foreground"># For WebServer</code><br/>
                        <code className="text-sm text-muted-foreground">python app.py</code><br/>
                        <code className="text-sm text-muted-foreground"># Then open: http://localhost:5000</code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground">
                  The system successfully helped users discover new books across different genres, 
                  improving user engagement and reading diversity through intelligent recommendations.
                </p>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button asChild className="w-full" variant="outline">
                    <a href="https://github.com/Mohan-Balaji/book-recommendation-system" target="_blank" rel="noopener noreferrer">
                      <Github className="h-4 w-4 mr-2" />
                      View Code
                    </a>
                  </Button>
                  <Button asChild className="w-full" variant="outline">
                    <a href="https://book-recommendation-system-hef5hdb3fyccccds.southindia-01.azurewebsites.net/" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Live Demo
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Sample Output</h3>
                <div className="bg-muted/20 p-3 rounded-lg border border-border text-sm">
                  <div className="text-foreground font-medium mb-2">---- Top 2 recommendations for 'Harry Potter':</div>
                  <div className="space-y-2">
                    <div className="border-l-2 border-primary pl-3">
                      <div className="text-foreground">1. ---- The Lord of the Rings</div>
                      <div className="text-muted-foreground text-xs">---- Author: J.R.R. Tolkien</div>
                      <div className="text-muted-foreground text-xs">--- Rating: 4.5 | ---- Similarity: 0.85</div>
                    </div>
                    <div className="border-l-2 border-primary pl-3">
                      <div className="text-foreground">2. ---- The Chronicles of Narnia</div>
                      <div className="text-muted-foreground text-xs">---- Author: C.S. Lewis</div>
                      <div className="text-muted-foreground text-xs">--- Rating: 4.3 | ---- Similarity: 0.78</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Python</p>
                      <p className="text-sm text-muted-foreground">Core language</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Scikit-learn</p>
                      <p className="text-sm text-muted-foreground">AI algorithms</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Pandas</p>
                      <p className="text-sm text-muted-foreground">Data manipulation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Azure</p>
                      <p className="text-sm text-muted-foreground">Cloud platform</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Inventory_Management_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Inventory Management System</h3>
              <p className="text-muted-foreground line-clamp-2">A comprehensive system for managing inventory and tracking stock levels.</p>
            </Link>
            
            <Link to="/Bee_Solar_Website" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Bee Solar Website</h3>
              <p className="text-muted-foreground line-clamp-2">A modern website showcasing solar energy solutions and services.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Book_Recommendation_System;

